import { useMemo } from "react";
import { getArDateKey } from "@/lib/timezone";

/**
 * Racha con "día libre": se permite 1 día sin clase por semana (lunes a domingo,
 * hora Argentina) sin cortar la racha. El día libre no suma. Hoy sin completar no corta.
 */
export function useStreak(completedDates: string[]) {
  return useMemo(() => {
    const done = new Set(completedDates);
    if (done.size === 0) return { current: 0, best: 0, freeDays: [] as string[] };

    const todayStr = getArDateKey();
    const freeDays = new Set<string>();

    // Racha actual: caminar hacia atrás
    let current = 0;
    let cursor = done.has(todayStr) ? todayStr : addDays(todayStr, -1);
    const usedWeeks = new Set<string>();
    let pending: string | null = null;
    const earliest = [...done].sort()[0];
    while (cursor >= earliest) {
      if (done.has(cursor)) {
        current++;
        if (pending) {
          freeDays.add(pending);
          pending = null;
        }
      } else {
        const wk = weekKey(cursor);
        if (usedWeeks.has(wk) || pending) break;
        usedWeeks.add(wk);
        pending = cursor;
      }
      cursor = addDays(cursor, -1);
    }

    // Mejor racha (y días libres históricos): caminar hacia adelante
    const asc = [...done].sort();
    let best = 0;
    let run = 0;
    const fwdWeeks = new Set<string>();
    let fwdPending: string | null = null;
    for (let d = asc[0]; d <= asc[asc.length - 1]; d = addDays(d, 1)) {
      if (done.has(d)) {
        run++;
        if (fwdPending) {
          freeDays.add(fwdPending);
          fwdPending = null;
        }
      } else {
        const wk = weekKey(d);
        if (run > 0 && !fwdPending && !fwdWeeks.has(wk)) {
          fwdWeeks.add(wk);
          fwdPending = d;
        } else {
          run = 0;
          fwdPending = null;
          fwdWeeks.clear();
        }
      }
      best = Math.max(best, run);
    }
    best = Math.max(best, current);

    return { current, best, freeDays: [...freeDays].sort() };
  }, [completedDates]);
}

function toUtc(key: string) {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}
function fromUtc(d: Date) {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}
export function addDays(key: string, n: number) {
  const d = toUtc(key);
  d.setUTCDate(d.getUTCDate() + n);
  return fromUtc(d);
}
/** Clave del lunes de la semana de la fecha. */
export function weekKey(key: string) {
  const d = toUtc(key);
  const dow = (d.getUTCDay() + 6) % 7; // lunes = 0
  d.setUTCDate(d.getUTCDate() - dow);
  return fromUtc(d);
}
